/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular } from "cms/icons";
import { useOrgSettings } from "hooks/use-org-settings";
import { DocumentCard } from "../../dialog/document-cover";
import { formatBytes } from "utils/format-bytes";
import { 
    getBasenameAndExtension,
    isFileTypeInSet,
    isValidDocumentExtension
} from "cms/files";
import { isArrayNotEmpty } from "utils/empty-exists";

const { faXmark } = regular;

const DocumentDropArea = (props) => {
    const newFile = props?.newFile;
    const setNewFile = props?.setNewFile;
    const setIsUploaded = props?.setIsUploaded;
    const fileType = props?.fileType;

    const [rejectionfiles, setRejectionfiles] = useState();
    const [tooMany, setTooMany] = useState();
    const { getOrgSettingsNumber } = useOrgSettings();
    const fileTypeAcceptFormat = {};
    fileTypeAcceptFormat[fileType] = [];
    const {
        getRootProps,
        getInputProps,
        // fileRejections,
    } = useDropzone({
        onDrop: (acceptedFiles, rejectedFiles) => {
            setNewFile(undefined);
            setTooMany(false);
            if (acceptedFiles.length > 1) {
                setTooMany(true);
                setIsUploaded(false);
            } else if (acceptedFiles.length > 0) {
                setTooMany(false);
                setRejectionfiles([]);
                const _file = acceptedFiles[0];
                const updatedFile = new File(
                    [_file],
                    _file?.name,
                    { type: _file.type, }
                );
                const fileWithPreview = Object.assign(
                    updatedFile, 
                    { preview: URL.createObjectURL(updatedFile) }
                );
                setNewFile(fileWithPreview);
                setIsUploaded(true);
            }
            if (rejectedFiles.length > 1) {
                setTooMany(true);
                setIsUploaded(false);
            } else if (rejectedFiles.length > 0) {
                const array = []; 
                rejectedFiles.map((rejection) => {
                    const file = rejection.file;
                    const errors = rejection.errors;
                    array.push(
                        <div key={file.path}>
                            {errors.map((error, index) => (
                                <div key={`${index}error.code`}>{error.message}</div>
                            ))}
                        </div>
                    );
                });
                setRejectionfiles(array);
            }
        },
        // accept: fileType ? fileTypeAcceptFormat : DEFAULT_ACCEPT,
        maxFiles: 1,
        // maxSize: uploadLimit,
        noClick: true,
        validator: (file) => {
            const file_name = file.name;
            const file_size = file.size;
            // eslint-disable-next-line no-unused-vars
            const [basename, ext] = getBasenameAndExtension(file_name);
            const uploadLimit = getOrgSettingsNumber("upload_limit_document");
            const max_chars = 64;
            const isDoc = isValidDocumentExtension(ext);
            const isKnownType = isFileTypeInSet(file, fileTypeAcceptFormat);
            if (!isKnownType) {
                return {
                    code: "type-not-allowed",
                    message: `Error: File type [${file.type}] is not allowed. Please try again.`
                };
            }
            if (!isDoc) {
                return {
                    code: "ext-not-allowed",
                    message: "Error: File extension is not allowed. Please try again."
                };
            }
            if (file_name.length > max_chars) {
                return {
                    code: "name-too-large",
                    message: `Error: File name is larger than ${max_chars} characters.`
                };
            }

            if (file_size > uploadLimit) {
                return {
                    code: "file-too-large",
                    message: `Error: File exceeds maximum size (${formatBytes(uploadLimit)}). Please try again.`
                };
            }
            // must return null if no error
            return null;
        },
    });
    const clear = () => {
        setNewFile(undefined);
        setTooMany(false);
        setIsUploaded(false);
        setRejectionfiles([]);
    };
    // const fileRejectionItems = fileRejections.map((rejection) => {
    //     const file = rejection.file;
    //     const errors = rejection.errors;
    //     return (
    //         <div key={file.path}>
    //             {errors.map((error, index) => (
    //                 <div key={`${index}error.code`}>{error.message}</div>
    //             ))}
    //         </div>
    //     )
    // });

    const handleRemoveDocument = () => {
        setNewFile(null);
        setIsUploaded(false);
    };
    return (
        <div className="file-drop-area" {...getRootProps()}>
            <input {...getInputProps()} id="upload-input" className="file-input" />
            {!newFile && (
                <div className="file-drop-text">
                    <div>
                        Drag and drop, or{" "}
                        <label htmlFor="upload-input" className="select-image-label">
                            select a document
                        </label>
                    </div>
                </div>
            )}
            {!tooMany && newFile && (
                <div className="file-preview">
                    <DocumentCard
                        displayWidth={36}
                        displayHeight={36}
                        fileExt={newFile.file_ext}
                        fontSize="3rem"
                    />
                    <div className="file-info">
                        <p>{newFile.name}</p>
                        <p>Size: {(newFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <FontAwesomeIcon
                        icon={faXmark}
                        onClick={handleRemoveDocument}
                        className="remove-img-icon"
                        aria-label="Remove document"
                    />
                </div>
            )}
            {tooMany &&
                <div className="file-drop-errors">
                    <div>Too many files.</div>
                    <button className="file-clear-button" onClick={clear} >clear</button>
                </div>
            }
            {!tooMany && isArrayNotEmpty(rejectionfiles) &&
                <div className="file-drop-errors">
                    <div>
                        <div>{rejectionfiles}</div>
                    </div>
                    <button className="file-clear-button" onClick={clear} >clear</button>
                </div>
            }
        </div>
    );
};

export { DocumentDropArea };
